import { useApolloClient, useQuery, type ApolloClient } from '@apollo/client';
import type { ResultOf } from '@newfront-insurance/graphql-frontend';
import { useEffect, useState } from 'react';

import type { SettingsEmployee } from './employee-utils';
import {
  SEARCH_SETTINGS_SKILL_GROUP_PEOPLE,
  type SettingsSkillGroupPeopleVariables,
  type SettingsSkillGroupPerson,
} from '../../../../queries/skill-administration';

export const PEOPLE_PAGE_SIZE = 30;
export const MAX_PASTED_EMAILS = 500;
/** "Select all" loads every result before selecting, so it is only offered for searches up to this size. */
export const MAX_SELECT_ALL = 500;
// The BFF caps pages and email filters at 100. Email batches stay smaller because persisted queries send their
// variables in the URL.
const FULL_PAGE_SIZE = 100;
const EMAIL_BATCH_SIZE = 50;
const QUIET_CONTEXT = { notifications: { preventErrorNotification: true } };

type PeopleData = ResultOf<typeof SEARCH_SETTINGS_SKILL_GROUP_PEOPLE>;
type PagingStatus = 'loadingMore' | 'loadMoreError' | 'loadingAll';

function toSettingsEmployee(person: SettingsSkillGroupPerson): SettingsEmployee {
  return {
    userUuid: person.userUuid,
    email: person.email,
    name: person.fullName,
    firstName: person.firstName ?? undefined,
    lastName: person.lastName ?? undefined,
    status: 'active',
  };
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, (index + 1) * size),
  );
}

async function findPeople(client: ApolloClient<object>, variables: SettingsSkillGroupPeopleVariables) {
  const result = await client.query({
    query: SEARCH_SETTINGS_SKILL_GROUP_PEOPLE,
    variables,
    fetchPolicy: 'no-cache',
    context: QUIET_CONTEXT,
  });
  return result.data.searchSkillGroupPeople.people.map(toSettingsEmployee);
}

/** Finds organization members by exact email, in batches small enough for one request each. */
async function findPeopleByEmail(client: ApolloClient<object>, groupId: string, emails: readonly string[]) {
  const batches = await Promise.all(
    chunk(emails, EMAIL_BATCH_SIZE).map((batch) =>
      findPeople(client, { groupId, query: null, emails: batch, first: FULL_PAGE_SIZE, after: null }),
    ),
  );
  return batches.flat();
}

/**
 * Pages through an organization's people as the BFF scopes and searches them, or lists just the people with the given
 * emails. The previous results stay available (as stale) until the new search's results arrive.
 */
export function useSkillGroupPeople({
  groupId,
  query,
  emails,
}: {
  groupId: string | null;
  query: string;
  /** Lowercase emails to list instead of searching. */
  emails: readonly string[] | null;
}) {
  const client = useApolloClient();
  const emailsKey = groupId && emails?.length ? emails.join(' ') : null;
  const variables = { groupId: groupId ?? '', query: query || null, first: PEOPLE_PAGE_SIZE, after: null };
  const { data, previousData, error, fetchMore, refetch, observable } = useQuery(SEARCH_SETTINGS_SKILL_GROUP_PEOPLE, {
    variables,
    skip: !groupId || emailsKey !== null,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    context: QUIET_CONTEXT,
  });
  // Apollo leaves networkStatus at fetchMore after a fetchMore fails, so paging status is kept here, per search.
  const search = JSON.stringify([variables.groupId, variables.query, emailsKey]);
  const [paging, setPaging] = useState<{ search: string; status: PagingStatus } | null>(null);
  const pagingStatus = paging?.search === search ? paging.status : null;
  const page = data?.searchSkillGroupPeople;
  const shown = (data ?? previousData)?.searchSkillGroupPeople;

  // A pasted list can hold more emails than one request carries (variables travel in the URL and the BFF takes 100
  // emails), so it is looked up in batches outside useQuery, and every match arrives at once.
  const [emailAttempt, setEmailAttempt] = useState(0);
  const emailSearch = emailsKey && JSON.stringify([groupId, emailsKey, emailAttempt]);
  const [emailResult, setEmailResult] = useState<{ search: string; people: SettingsEmployee[] | null } | null>(null);
  /** Undefined while the lookup runs, null if it failed. */
  const emailMatches = emailResult?.search === emailSearch ? emailResult.people : undefined;

  useEffect(() => {
    if (!groupId || !emailsKey || !emailSearch) return;
    let isCurrent = true;
    findPeopleByEmail(client, groupId, emailsKey.split(' ')).then(
      (people) => {
        if (isCurrent) setEmailResult({ search: emailSearch, people });
      },
      () => {
        if (isCurrent) setEmailResult({ search: emailSearch, people: null });
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [client, groupId, emailsKey, emailSearch]);

  const finishPaging = (status: 'loadMoreError' | null) =>
    setPaging((current) => (current?.search === search ? status && { search, status } : current));

  const isCurrentSearch = () =>
    observable.variables?.groupId === variables.groupId && observable.variables.query === variables.query;

  // With no-cache, Apollo merges a page into whatever the query shows when the page arrives. Only append a page that
  // continues the current list: that drops pages from an abandoned search and duplicate requests for the same cursor.
  // `previous` is undefined while a newer search loads.
  const appendPage = (
    previous: PeopleData | undefined,
    {
      fetchMoreResult,
      variables: pageVariables,
    }: { fetchMoreResult: PeopleData; variables: SettingsSkillGroupPeopleVariables },
  ) =>
    previous && isCurrentSearch() && previous.searchSkillGroupPeople.nextCursor === pageVariables.after
      ? {
          searchSkillGroupPeople: {
            ...fetchMoreResult.searchSkillGroupPeople,
            people: [...previous.searchSkillGroupPeople.people, ...fetchMoreResult.searchSkillGroupPeople.people],
          },
        }
      : (previous as PeopleData);

  const fetchNextPage = async (after: string, first = PEOPLE_PAGE_SIZE) => {
    const result = await fetchMore({ variables: { after, first }, updateQuery: appendPage });
    return result.data.searchSkillGroupPeople;
  };

  const loadMore = () => {
    if (!page?.nextCursor || (pagingStatus && pagingStatus !== 'loadMoreError')) return;
    setPaging({ search, status: 'loadingMore' });
    fetchNextPage(page.nextCursor).then(
      () => finishPaging(null),
      () => finishPaging('loadMoreError'),
    );
  };

  /** Loads every remaining result, for "Select all". Resolves null if the search changed; rejects if a page fails. */
  const loadAll = async (): Promise<SettingsEmployee[] | null> => {
    if (emailSearch) return emailMatches ?? null;
    if (!page || pagingStatus === 'loadingMore' || pagingStatus === 'loadingAll') return null;
    setPaging({ search, status: 'loadingAll' });
    const loadFrom = async (
      after: string | null,
      loaded: SettingsSkillGroupPerson[],
    ): Promise<SettingsSkillGroupPerson[] | null> => {
      if (!after) return loaded;
      const next = await fetchNextPage(after, FULL_PAGE_SIZE);
      return isCurrentSearch() ? loadFrom(next.nextCursor, [...loaded, ...next.people]) : null;
    };
    try {
      const people = await loadFrom(page.nextCursor, page.people);
      return people?.map(toSettingsEmployee) ?? null;
    } finally {
      finishPaging(null);
    }
  };

  /** Fetches the first page for a search directly, for acting on it before the list catches up. */
  const searchPeople = (text: string) => findPeople(client, { ...variables, query: text || null });

  const pagedPeople = (shown?.people ?? []).map(toSettingsEmployee);
  const foundEmails = new Set(emailMatches?.map((person) => person.email.toLowerCase()));
  const listed = emailSearch
    ? {
        people: emailMatches ?? pagedPeople,
        totalCount: emailMatches?.length ?? 0,
        hasMore: false,
        isLoading: emailMatches === undefined,
        error: emailMatches === null,
        /** Pasted emails that match no one in the organization. */
        unmatchedEmails: emailMatches ? (emails ?? []).filter((email) => !foundEmails.has(email)) : [],
        retry: () => setEmailAttempt((count) => count + 1),
      }
    : {
        people: pagedPeople,
        totalCount: shown?.totalCount ?? 0,
        hasMore: Boolean(page?.nextCursor),
        isLoading: groupId !== null && !data && !error,
        error: Boolean(error),
        unmatchedEmails: [],
        retry: () => {
          refetch().catch(() => undefined);
        },
      };

  return {
    ...listed,
    isLoadingMore: pagingStatus === 'loadingMore',
    isLoadingAll: pagingStatus === 'loadingAll',
    loadMoreError: pagingStatus === 'loadMoreError',
    loadMore,
    loadAll,
    searchPeople,
  };
}
